// src/components/properties/PropertySettingsTab.js

import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../api';
import toast from 'react-hot-toast';
import { Clock, Coffee, Wifi, Save, Loader2, Printer } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

const PropertySettingsTab = ({ property }) => {
    
    // Initialize state robustly (safely handles MySQL 1/0 or booleans)
    const [settings, setSettings] = useState({
        is_overtime_enabled: Number(property?.settings_overtime_enabled) === 1 || property?.settings_overtime_enabled === true,
        overtime_threshold: parseFloat(property?.settings_overtime_threshold) || 8.00,
        auto_meal_enabled: Number(property?.settings_auto_meal_enabled) === 1 || property?.settings_auto_meal_enabled === true,
        auto_meal_threshold: parseFloat(property?.settings_auto_meal_threshold) || 6.00,
        auto_meal_minutes: parseInt(property?.settings_auto_meal_minutes) || 30,
        guest_wifi_ssid: property?.settings_guest_wifi_ssid || '',
        guest_wifi_password: property?.settings_guest_wifi_password || '',
        guest_wifi_encryption: property?.settings_guest_wifi_encryption || 'WPA2'
    });
    
    const [savingSection, setSavingSection] = useState(null);
    const qrRef = useRef(null);

    // ONLY re-sync if the user clicks a completely different property.
    // This prevents the parent's stale cache from overwriting our correct local state.
    useEffect(() => {
        if (property) {
            setSettings({
                is_overtime_enabled: Number(property.settings_overtime_enabled) === 1 || property.settings_overtime_enabled === true,
                overtime_threshold: parseFloat(property.settings_overtime_threshold) || 8.00,
                auto_meal_enabled: Number(property.settings_auto_meal_enabled) === 1 || property.settings_auto_meal_enabled === true,
                auto_meal_threshold: parseFloat(property.settings_auto_meal_threshold) || 6.00,
                auto_meal_minutes: parseInt(property.settings_auto_meal_minutes) || 30,
                guest_wifi_ssid: property.settings_guest_wifi_ssid || '',
                guest_wifi_password: property.settings_guest_wifi_password || '',
                guest_wifi_encryption: property.settings_guest_wifi_encryption || 'WPA2'
            });
        }
    }, [property?.id]);

    const handleSave = async (sectionName, overrideSettings = null) => {
        setSavingSection(sectionName);
        const dataToSave = overrideSettings || settings; 
        try {
            await api.updatePropertySettings(property.id, dataToSave);
            
            // "Cache Patch": Quietly update the parent's object reference in memory.
            // This ensures if the parent re-renders, it uses our newly saved data 
            // instead of reverting the UI back to the old database state.
            property.settings_overtime_enabled = dataToSave.is_overtime_enabled ? 1 : 0;
            property.settings_overtime_threshold = dataToSave.overtime_threshold;
            property.settings_auto_meal_enabled = dataToSave.auto_meal_enabled ? 1 : 0;
            property.settings_auto_meal_threshold = dataToSave.auto_meal_threshold;
            property.settings_auto_meal_minutes = dataToSave.auto_meal_minutes;
            property.settings_guest_wifi_ssid = dataToSave.guest_wifi_ssid;
            property.settings_guest_wifi_password = dataToSave.guest_wifi_password;
            property.settings_guest_wifi_encryption = dataToSave.guest_wifi_encryption;

            const sectionLabel = sectionName === 'overtime' ? 'Overtime' : sectionName === 'meal' ? 'Meal Deduction' : 'Guest WiFi';
            toast.success(`${sectionLabel} settings saved!`);
        } catch (err) {
            toast.error("Failed to save settings.");
        } finally {
            setSavingSection(null);
        }
    };

    // --- SMART TOGGLES ---
    const handleToggleOvertime = (e) => {
        const isChecked = e.target.checked;
        const newSettings = { ...settings, is_overtime_enabled: isChecked };
        setSettings(newSettings);
        if (!isChecked) handleSave('overtime', newSettings);
    };

    const handleToggleMeal = (e) => {
        const isChecked = e.target.checked;
        const newSettings = { ...settings, auto_meal_enabled: isChecked };
        setSettings(newSettings);
        if (!isChecked) handleSave('meal', newSettings);
    };

    // --- VALIDATION LOGIC ---
    const isOvertimeValid = settings.overtime_threshold && String(settings.overtime_threshold).trim() !== '' && parseFloat(settings.overtime_threshold) > 0;
    const isMealValid = settings.auto_meal_threshold && String(settings.auto_meal_threshold).trim() !== '' && parseFloat(settings.auto_meal_threshold) > 0 &&
                        settings.auto_meal_minutes && String(settings.auto_meal_minutes).trim() !== '' && parseInt(settings.auto_meal_minutes) > 0;

    // --- WIFI QR CODE ---
    const isWifiConfigured = settings.guest_wifi_ssid && settings.guest_wifi_ssid.trim() !== '';
    const wifiQRValue = isWifiConfigured 
        ? `WIFI:T:${settings.guest_wifi_encryption};S:${settings.guest_wifi_ssid};P:${settings.guest_wifi_password};;`
        : '';

    const handlePrintQR = () => {
        if (!isWifiConfigured) return;

        const canvas = qrRef.current?.querySelector('canvas');
        if (!canvas) return;

        const qrDataUrl = canvas.toDataURL('image/png');

        const printWindow = window.open('', '_blank', 'width=500,height=700');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>WiFi QR Code - ${property.name}</title>
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
                    
                    * { margin: 0; padding: 0; box-sizing: border-box; }
                    body { 
                        font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif; 
                        display: flex; 
                        justify-content: center; 
                        align-items: center; 
                        min-height: 100vh;
                        background: #f1f5f9;
                        padding: 20px;
                    }
                    .card {
                        text-align: center; 
                        padding: 40px 36px;
                        background: white;
                        border-radius: 20px;
                        box-shadow: 0 4px 24px rgba(0,0,0,0.08);
                        max-width: 380px;
                        width: 100%;
                        position: relative;
                        overflow: hidden;
                    }
                    .house-roof {
                        position: absolute;
                        top: 0;
                        left: 0;
                        width: 100px;
                        height: 100px;
                        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                        clip-path: polygon(0 0, 100% 0, 0 100%);
                    }
                    .house-icon {
                        position: absolute;
                        top: 12px;
                        left: 12px;
                        z-index: 1;
                    }
                    .house-icon svg {
                        width: 28px;
                        height: 28px;
                        fill: white;
                        opacity: 0.9;
                    }
                    .icon-circle {
                        width: 64px;
                        height: 64px;
                        background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin: 0 auto 20px;
                        position: relative;
                        z-index: 2;
                    }
                    .icon-circle svg {
                        width: 32px;
                        height: 32px;
                        fill: white;
                    }
                    h1 { 
                        font-size: 1.5rem; 
                        font-weight: 700;
                        color: #0f172a; 
                        margin-bottom: 6px;
                    }
                    .subtitle { 
                        font-size: 0.9rem; 
                        color: #64748b; 
                        margin-bottom: 28px;
                        font-weight: 400;
                    }
                    .qr-wrapper {
                        display: flex;
                        justify-content: center;
                        margin-bottom: 28px;
                    }
                    .qr-wrapper img {
                        width: 180px;
                        height: 180px;
                        border: 8px solid #f8fafc;
                        border-radius: 12px;
                        box-shadow: 0 2px 12px rgba(0,0,0,0.06);
                    }
                    .instructions {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        background: #f0fdf4;
                        border: 1px solid #bbf7d0;
                        border-radius: 10px;
                        padding: 12px 16px;
                        margin-bottom: 24px;
                        text-align: left;
                    }
                    .instructions-icon {
                        width: 32px;
                        height: 32px;
                        background: #22c55e;
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        flex-shrink: 0;
                    }
                    .instructions-icon svg {
                        width: 16px;
                        height: 16px;
                        fill: white;
                    }
                    .instructions p {
                        font-size: 0.8rem;
                        color: #166534;
                        margin: 0;
                        line-height: 1.4;
                    }
                    .network-section {
                        background: #f8fafc;
                        border-radius: 12px;
                        padding: 20px;
                        margin-bottom: 24px;
                    }
                    .network-label {
                        font-size: 0.7rem;
                        color: #94a3b8;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin-bottom: 6px;
                        font-weight: 500;
                    }
                    .network-name {
                        font-size: 1.3rem;
                        font-weight: 700;
                        color: #0f172a;
                        font-family: 'Courier New', monospace;
                        letter-spacing: 1px;
                    }
                    .password-section {
                        margin-top: 16px;
                        padding-top: 16px;
                        border-top: 1px solid #e2e8f0;
                    }
                    .password-label {
                        font-size: 0.65rem;
                        color: #94a3b8;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        margin-bottom: 4px;
                    }
                    .password-value {
                        font-size: 0.95rem;
                        font-weight: 600;
                        color: #0f172a;
                        font-family: 'Courier New', monospace;
                    }
                    .footer {
                        font-size: 0.75rem;
                        color: #94a3b8;
                        padding-top: 16px;
                        border-top: 1px solid #e2e8f0;
                    }
                    @media print {
                        body { 
                            background: white; 
                            padding: 0;
                        }
                        .card { 
                            box-shadow: none;
                            border: 1px solid #e2e8f0;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="card">
                    <div class="house-roof"></div>
                    <div class="house-icon">
                        <svg viewBox="0 0 24 24"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                    </div>
                    
                    <div class="icon-circle">
                        <svg viewBox="0 0 24 24"><path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/></svg>
                    </div>
                    <h1>${property.name}</h1>
                    <p class="subtitle">Guest WiFi Access</p>
                    
                    <div class="instructions">
                        <div class="instructions-icon">
                            <svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>
                        </div>
                        <p>Point your phone camera at the QR code to connect automatically</p>
                    </div>

                    <div class="qr-wrapper">
                        <img src="${qrDataUrl}" alt="WiFi QR Code" />
                    </div>

                    <div class="network-section">
                        <p class="network-label">Network Name</p>
                        <p class="network-name">${settings.guest_wifi_ssid}</p>
                        <div class="password-section">
                            <p class="password-label">Password</p>
                            <p class="password-value">${settings.guest_wifi_password || 'None'}</p>
                        </div>
                    </div>

                    <p class="footer">WiFi access provided by ${property.name}</p>
                </div>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => printWindow.print(), 300);
    };

    if (!property) return null;

    return (
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0' }}>
            <h3 style={{ margin: '0 0 24px 0', fontSize: '1.2rem', color: '#0f172a' }}>Payroll & Automation Rules</h3>
            
            {/* --- OVERTIME SETTINGS --- */}
            <div style={{ border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', marginBottom: '20px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Clock size={22} color="#1798c1" />
                        <div>
                            <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b' }}>Overtime Rules</strong>
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Calculate overtime in payroll reports</span>
                        </div>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* ONLY VISIBLE WHEN TOGGLE IS ON */}
                        {settings.is_overtime_enabled && (
                            <button 
                                onClick={() => handleSave('overtime')} 
                                disabled={savingSection === 'overtime' || !isOvertimeValid}
                                title="Save Overtime Rules"
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    width: '36px', height: '36px', borderRadius: '8px', 
                                    border: 'none',
                                    backgroundColor: isOvertimeValid ? '#1798c1' : '#cbd5e1', 
                                    color: 'white', 
                                    cursor: isOvertimeValid ? 'pointer' : 'not-allowed',
                                    boxShadow: isOvertimeValid ? '0 2px 4px rgba(23, 152, 193, 0.3)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {savingSection === 'overtime' ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                            </button>
                        )}
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={settings.is_overtime_enabled} 
                                onChange={handleToggleOvertime} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>
                
                {settings.is_overtime_enabled && (
                    <div style={{ marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            Daily Overtime Threshold (Hours)
                        </label>
                        <input 
                            type="number" 
                            step="0.5"
                            className="form-input" 
                            style={{ width: '150px', background: 'white', borderColor: !isOvertimeValid ? '#ef4444' : '#e2e8f0' }}
                            value={settings.overtime_threshold} 
                            onChange={(e) => setSettings({...settings, overtime_threshold: e.target.value})}
                        />
                        <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '8px', marginBottom: 0 }}>
                            Hours worked beyond this threshold in a single calendar day will be marked as overtime (OT).
                        </p>
                    </div>
                )}
            </div>

            {/* --- MEAL DEDUCTION SETTINGS --- */}
            <div style={{ border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', marginBottom: '20px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Coffee size={22} color="#d97706" />
                        <div>
                            <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b' }}>Auto-Meal Deduction</strong>
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Automatically subtract unpaid lunch breaks</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                        {/* ONLY VISIBLE WHEN TOGGLE IS ON */}
                        {settings.auto_meal_enabled && (
                            <button 
                                onClick={() => handleSave('meal')} 
                                disabled={savingSection === 'meal' || !isMealValid}
                                title="Save Meal Rules"
                                style={{ 
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                    width: '36px', height: '36px', borderRadius: '8px', 
                                    border: 'none',
                                    backgroundColor: isMealValid ? '#1798c1' : '#cbd5e1', 
                                    color: 'white', 
                                    cursor: isMealValid ? 'pointer' : 'not-allowed',
                                    boxShadow: isMealValid ? '0 2px 4px rgba(23, 152, 193, 0.3)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {savingSection === 'meal' ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                            </button>
                        )}
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={settings.auto_meal_enabled} 
                                onChange={handleToggleMeal} 
                            />
                            <span className="slider"></span>
                        </label>
                    </div>
                </div>

                {settings.auto_meal_enabled && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                Minimum Shift Length (Hours)
                            </label>
                            <input 
                                type="number" 
                                step="0.5"
                                className="form-input" 
                                style={{ width: '180px', background: 'white', borderColor: !isMealValid ? '#ef4444' : '#e2e8f0' }}
                                value={settings.auto_meal_threshold} 
                                onChange={(e) => setSettings({...settings, auto_meal_threshold: e.target.value})}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                                Minutes to Deduct
                            </label>
                            <input 
                                type="number" 
                                step="5"
                                className="form-input" 
                                style={{ width: '180px', background: 'white', borderColor: !isMealValid ? '#ef4444' : '#e2e8f0' }}
                                value={settings.auto_meal_minutes} 
                                onChange={(e) => setSettings({...settings, auto_meal_minutes: e.target.value})}
                            />
                        </div>
                        <div style={{ width: '100%' }}>
                            <p style={{ fontSize: '0.85rem', color: '#1798c1', margin: 0, backgroundColor: '#f0f9ff', padding: '10px', borderRadius: '6px', border: '1px solid #bae6fd' }}>
                                <strong>Example:</strong> If a shift is longer than <strong>{settings.auto_meal_threshold || 'X'} hours</strong>, the system will automatically subtract <strong>{settings.auto_meal_minutes || 'Y'} minutes</strong> from their total paid time.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* --- GUEST WIFI SETTINGS --- */}
            <div style={{ border: '1px solid #e2e8f0', padding: '20px', borderRadius: '10px', background: '#f8fafc' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Wifi size={22} color="#16a34a" />
                        <div>
                            <strong style={{ display: 'block', fontSize: '1.05rem', color: '#1e293b' }}>Guest WiFi</strong>
                            <span style={{ fontSize: '0.85rem', color: '#64748b' }}>WiFi credentials for guest access</span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button 
                            onClick={handlePrintQR}
                            disabled={!isWifiConfigured}
                            title="Print QR Code"
                            style={{ 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                width: '36px', height: '36px', borderRadius: '8px', 
                                border: isWifiConfigured ? '1px solid #16a34a' : '1px solid #cbd5e1',
                                backgroundColor: isWifiConfigured ? 'white' : '#f8fafc', 
                                color: isWifiConfigured ? '#16a34a' : '#94a3b8', 
                                cursor: isWifiConfigured ? 'pointer' : 'not-allowed',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            <Printer size={18} />
                        </button>
                        <button 
                            onClick={() => handleSave('wifi')} 
                            disabled={savingSection === 'wifi'}
                            title="Save WiFi Settings"
                            style={{ 
                                display: 'flex', alignItems: 'center', justifyContent: 'center', 
                                width: '36px', height: '36px', borderRadius: '8px', 
                                border: 'none',
                                backgroundColor: '#16a34a', 
                                color: 'white', 
                                cursor: 'pointer',
                                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {savingSection === 'wifi' ? <Loader2 className="spin" size={18} /> : <Save size={18} />}
                        </button>
                    </div>
                </div>

                {/* Hidden QR Code for printing */}
                {isWifiConfigured && (
                    <div ref={qrRef} style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                        <QRCodeCanvas value={wifiQRValue} size={200} level="M" includeMargin={false} />
                    </div>
                )}

                {/* Input Fields */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginTop: '20px', borderTop: '1px solid #e2e8f0', paddingTop: '16px' }}>
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            WiFi Network Name (SSID)
                        </label>
                        <input 
                            type="text" 
                            className="form-input" 
                            style={{ width: '100%', background: 'white' }}
                            placeholder="e.g., BestStar_Guest"
                            value={settings.guest_wifi_ssid} 
                            onChange={(e) => setSettings({...settings, guest_wifi_ssid: e.target.value})}
                        />
                    </div>
                    <div style={{ flex: '1 1 200px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            Password
                        </label>
                        <input 
                            type="text" 
                            className="form-input" 
                            style={{ width: '100%', background: 'white' }}
                            placeholder="WiFi password"
                            value={settings.guest_wifi_password} 
                            onChange={(e) => setSettings({...settings, guest_wifi_password: e.target.value})}
                        />
                    </div>
                    <div style={{ flex: '0 0 160px' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>
                            Encryption
                        </label>
                        <select
                            className="form-input"
                            style={{ width: '100%', background: 'white' }}
                            value={settings.guest_wifi_encryption}
                            onChange={(e) => setSettings({...settings, guest_wifi_encryption: e.target.value})}
                        >
                            <option value="WPA">WPA</option>
                            <option value="WPA2">WPA2</option>
                            <option value="WPA3">WPA3</option>
                            <option value="NONE">None (Open)</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PropertySettingsTab;
